import { useState, type ChangeEvent, type FormEvent } from 'react';
import type { z } from 'zod';
import { ApiRequestError } from './api';

type Values = Record<string, string>;

/**
 * Small form helper: validates with the same Zod schema the API uses, then maps any server
 * field errors (400 details) back onto the fields. Values are strings, as in the DOM.
 */
export function useZodForm<S extends z.ZodType, V extends Values>(schema: S, initial: V) {
  const [values, setValues] = useState<V>(initial);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const field = (name: keyof V & string) => ({
    name,
    value: values[name],
    error: errors[name],
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      setValues((v) => ({ ...v, [name]: e.target.value }));
      if (errors[name]) setErrors((err) => ({ ...err, [name]: undefined }));
    },
  });

  const handleSubmit =
    (onValid: (data: z.infer<S>) => Promise<void> | void) => async (e: FormEvent) => {
      e.preventDefault();
      setFormError(null);
      const parsed = schema.safeParse(values);
      if (!parsed.success) {
        const next: Record<string, string> = {};
        for (const issue of parsed.error.issues) {
          const key = String(issue.path[0] ?? '');
          next[key] ??= issue.message;
        }
        setErrors(next);
        // Move focus to the first invalid field for keyboard and screen-reader users.
        const first = Object.keys(next)[0];
        if (first) (document.querySelector(`[name="${first}"]`) as HTMLElement | null)?.focus();
        return;
      }
      setErrors({});
      setSubmitting(true);
      try {
        await onValid(parsed.data);
      } catch (err) {
        if (err instanceof ApiRequestError) {
          if (err.details.length) {
            setErrors(
              Object.fromEntries(err.details.map((d) => [d.path.split('.')[0]!, d.message])),
            );
          }
          setFormError(err.message);
        } else {
          setFormError('Something went wrong. Please try again.');
        }
      } finally {
        setSubmitting(false);
      }
    };

  return { values, setValues, field, errors, formError, setFormError, submitting, handleSubmit };
}
